import { memo, useEffect, useState } from 'react';
import type { FilePondProps } from 'react-filepond';
import { FilePond } from 'react-filepond';
import { cn } from '~/utils/cn';
import { s3Upload, s3UploadWithSignature } from '~/utils/s3';
import { useToast } from './ui/use-toast';

function PDFUpload({
  initialFileUrl,
  isTemporary,
  url,
  fields,
  onUploaded,
  ...opts
}: {
  initialFileUrl?: string;
  isTemporary?: boolean;
  url?: string;
  fields?: Record<string, string>;
  onUploaded?: (fileName: string) => void;
} & FilePondProps) {
  const { toast } = useToast();

  const [isVisible, setIsVisible] = useState(false);

  // prevent flash of unstyled content: https://github.com/pqina/react-filepond/issues/110
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const server: FilePondProps['server'] = {
    // load: (source, load, error, progress, abort, headers) => {
    //   cloudinaryDownload(source, load).catch((e) => error(e));

    //   return {
    //     abort: () => {
    //       abort();
    //     }
    //   };
    // },
    revert: null,
    process: (
      fieldName,
      file,
      metadata,
      load,
      error,
      progress,
      abort,
      transfer,
      options
    ) => {
      let request = new XMLHttpRequest();
      if (isTemporary) {
        if (!url || !fields) {
          throw new Error('url and fields are required for temporary uploads');
        }
        s3UploadWithSignature(request, file as File, url, fields, progress, load, error);
      } else {
        s3Upload(request, file as File, progress, load, error);
      }
      return {
        abort: () => {
          // This function is entered if the user has tapped the cancel button
          request.abort();

          // Let FilePond know the request has been cancelled
          abort();
        }
      };
    }
  };

  let files: FilePondProps['files'] = undefined;

  if (initialFileUrl) {
    const initialFileUrlParsed = new URL(initialFileUrl);
    const size = initialFileUrlParsed.searchParams.get('size')
      ? parseInt(initialFileUrlParsed.searchParams.get('size')!)
      : 0;

    files = [
      {
        source: initialFileUrl,
        options: {
          type: 'local',
          file: {
            name: initialFileUrlParsed.pathname.split('/').pop() ?? 'file.pdf',
            size: size,
            type: 'application/pdf'
          }
        }
      }
    ];
  }

  return (
    <div className={cn(!isVisible && '!mb-4 h-[76px] w-full rounded-lg bg-[#f1f0ef]')}>
      <div className={isVisible ? 'visible' : 'invisible'}>
        <FilePond
          credits={false}
          server={server}
          acceptedFileTypes={['application/pdf']}
          onprocessfile={(error, file) => {
            if (!error) {
              onUploaded?.(file.serverId);
            }
          }}
          onerror={() => {
            toast({
              variant: 'destructive',
              title: 'Your file could not be uploaded.',
              description:
                'Please ensure you are uploading a PDF file under 20 mb and try again.'
            });
          }}
          labelIdle="Drag & Drop your PDF or <span class='filepond--label-action'>Browse</span>"
          files={files}
          {...opts}
        />
      </div>
    </div>
  );
}

// prevent re-rendering when the parent changes
export const PDFUploadWidget = memo(PDFUpload);
